// import { Fragment } from 'react/jsx-runtime';
import { useEffect, useMemo, useState } from 'react';

import {
  Cross1Icon,
  DotsVerticalIcon,
  MinusIcon,
  Pencil1Icon,
  PlusIcon,
  ResetIcon
} from '@radix-ui/react-icons';
import {
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  InputField,
  Label,
  Radio,
  Text
} from '@raystack/apsara';

import { divideRange } from '@src/components/Colormaps/ColorScale';
import { For, Show } from '@src/hoc';
import { Portal } from '@src/hoc/Portal';
import { rangeGenerator } from '@src/utils/commonUtils';
import { COLORMAP_TYPES } from '@src/utils/constants/APP';

import styles from './styles.module.css';

interface LineStrokeProps extends React.SVGProps<SVGSVGElement> {}
type bucket = {
  color: string;
  interval: [number, number];
};
interface DynamicColorScaleProps {
  container?: HTMLElement;
  side?: 'left' | 'right';
  defaultColorData: string[];
  defaultRange: string[];
  range: string[];
  buckets: bucket[];
  onRescaleChange: (value: string[]) => void;
  onBucketChange: (bucket: bucket[]) => void;
}

interface LinerGradientProps {
  continuousColor: string[];
  ticks: number[];
}
const DEFAULT_TICK_INTERVALS = 5;
const DEFAULT_BUCKET_INTERVALS = 7;

const LineStroke: React.FC<LineStrokeProps> = props => {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='2'
      height='6'
      viewBox='0 0 2 6'
      fill='none'
      {...props}
    >
      <path
        d='M0.666016 1L0.666015 5'
        stroke='currentColor'
        strokeLinecap='round'
      />
    </svg>
  );
};

export function reduceColors(gradient: string[], n: number): string[] {
  if (n <= 0) {
    throw new Error('Number of colors must be greater than 0.');
  }
  if (n > gradient.length) {
    throw new Error(
      'Number of colors cannot be greater than the length of the gradient.'
    );
  }

  const step = (gradient.length - 1) / (n - 1);
  const reducedGradient: string[] = [];

  for (let i = 0; i < n; i++) {
    const index = Math.round(i * step);
    reducedGradient.push(gradient[index]);
  }

  return reducedGradient;
}

export const LinearGradientColorScale = ({
  ticks,
  continuousColor
}: LinerGradientProps) => {
  return (
    <Flex
      direction='column'
      gap='extra-small'
      align='start'
      style={{ width: '100%' }}
    >
      <div
        className={styles.gradient}
        style={{
          background: `linear-gradient(to right, ${continuousColor.join(', ')})`
        }}
      />
      <Flex align='start' justify='between' style={{ width: '100%' }}>
        <For each={ticks}>
          {tick => (
            <Text size={2} weight={400} key={tick} variant='secondary'>
              {tick}
            </Text>
          )}
        </For>
      </Flex>
    </Flex>
  );
};

export const DynamicColorScale = ({
  container,
  side = 'left',
  defaultColorData = [],
  defaultRange = ['-1', '1'],
  range = ['-1', '1'],
  buckets = [],
  onBucketChange,
  onRescaleChange
}: DynamicColorScaleProps) => {
  // local states
  const [continuousColor, setContinuousColor] = useState<string[]>([
    ...defaultColorData
  ]);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [editType, setEditType] = useState<string>(
    COLORMAP_TYPES.rangeColormap
  );
  const [intervals, setIntervals] = useState<number>(DEFAULT_BUCKET_INTERVALS);

  //   for rendering ticks of scale
  const ticks = useMemo(() => {
    return rangeGenerator(
      Number(range[0]),
      Number(range[1]),
      DEFAULT_TICK_INTERVALS
    );
  }, [range]);

  //   for generating bucket data
  useEffect(() => {
    const [min, max] = range;

    const bucketIntervals = divideRange(Number(min), Number(max), intervals);

    const colors = reduceColors(defaultColorData, intervals);
    const BucketsArray = bucketIntervals.map((interval, index) => {
      return {
        color: colors[index],
        interval: interval
      };
    }) as bucket[];
    onBucketChange(BucketsArray);
  }, [range, intervals, defaultColorData]);

  //   for text field validation
  const rangeValidation = useMemo(() => {
    const inputPatternCheck = /^-?\d*\.?\d*$/;
    const isPatterValid =
      inputPatternCheck.test(range[0]) && inputPatternCheck.test(range[1]);

    return isPatterValid;
  }, [range]);

  //   range change handler
  const rangeChangeHandler = (LowerLImit: string, UpperLimit: string) => {
    onRescaleChange([LowerLImit, UpperLimit]);
  };

  //   change edit type state
  const EditTypeHandler = (value: string) => {
    setEditType(value);
  };

  const setIntervalHandler = (action: string) => {
    if (action === 'dec' && intervals > 2) {
      setIntervals(intervals - 1);
    }
    if (action === 'inc') {
      setIntervals(intervals + 1);
    }
  };

  //   reset all states
  const resetChanges = () => {
    onRescaleChange(defaultRange);
    setEditType(COLORMAP_TYPES.rangeColormap);
    setContinuousColor([...defaultColorData]);
    setIntervals(DEFAULT_BUCKET_INTERVALS);
  };

  //   reset and close editing mode
  const exitEditingMode = () => {
    resetChanges();
    setEditMode(false);
  };

  return (
    <Portal container={container}>
      <Flex
        direction='column'
        align='start'
        className={styles.wrapper}
        data-side={side}
      >
        {/* HEADER */}
        <Flex align='center' justify='between' className={styles.header}>
          <Show when={!editMode}>
            <Text size={2} weight={400}>
              Scale of given raster
            </Text>
          </Show>

          <Show when={editMode}>
            <Text size={4} weight={500} variant='primary'>
              Edit Scale
            </Text>
          </Show>

          <Show when={!editMode}>
            <DropdownMenu placement='top'>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant='text'
                  color='neutral'
                  style={{ padding: 0 }}
                  data-test-id='color-scale-dropdown-btn'
                >
                  <DotsVerticalIcon />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                <DropdownMenu.Item
                  onClick={() => setEditMode(true)}
                  data-test-id='enable-color-editing-btn'
                >
                  <Flex gap='small'>
                    <Pencil1Icon /> Edit Scale
                  </Flex>
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu>
          </Show>
          <Show when={editMode}>
            <Flex
              gap='small'
              style={{ color: 'var(--rs-color-foreground-base-secondary)' }}
            >
              <IconButton
                onClick={resetChanges}
                data-test-id='reset-changes-btn'
                size={3}
              >
                <ResetIcon />
              </IconButton>
              <IconButton
                size={3}
                onClick={exitEditingMode}
                data-test-id='exit-editing-mode'
              >
                <Cross1Icon />
              </IconButton>
            </Flex>
          </Show>
        </Flex>

        {/* SCALE OPTIONS */}
        <Show when={editMode}>
          <Radio value={editType} onValueChange={EditTypeHandler}>
            <Flex align='stretch' gap='extra-large'>
              <Flex gap='small'>
                <Radio.Item value={COLORMAP_TYPES.rangeColormap} />
                <Label className={styles.label}>Continuous</Label>
              </Flex>
              <Flex gap='small'>
                <Radio.Item value={COLORMAP_TYPES.bucketColormap} />
                <Label className={styles.label}>
                  Continuous with intervals
                </Label>
              </Flex>
            </Flex>
          </Radio>
        </Show>

        {/* Linear GRADIENTS */}
        <Show when={editType === COLORMAP_TYPES.rangeColormap}>
          <LinearGradientColorScale
            ticks={ticks}
            continuousColor={continuousColor}
          />
        </Show>

        {/* BUCKET INTERVAL COLORS */}
        <Show when={editType === COLORMAP_TYPES.bucketColormap}>
          <Flex
            direction='column'
            align='stretch'
            gap='extra-small'
            style={{ width: '100%' }}
          >
            <Flex align='center'>
              {buckets.map(i => (
                <div
                  key={i.color}
                  style={{ background: `${i.color}` }}
                  className={styles.bucketColor}
                />
              ))}
            </Flex>
            <Flex justify='between' align='center'>
              <Text>{range[0]}</Text>
              <For each={[...Array(intervals - 2)]}>
                {() => (
                  <Flex style={{ flex: '0 1 0' }}>
                    <LineStroke className={styles.lineStroke} />
                  </Flex>
                )}
              </For>
              <Text>{range.at(-1)}</Text>
            </Flex>
          </Flex>
        </Show>

        {/* Interval incrementor */}
        <Show when={editType === COLORMAP_TYPES.bucketColormap}>
          <Flex justify='between' align='center' style={{ width: '100%' }}>
            <Text size={1} weight={500} variant='secondary'>
              Number of intervals
            </Text>
            <Flex gap='medium' style={{ padding: 2 }} align='center'>
              <Button
                size='small'
                variant='outline'
                color='neutral'
                style={{ padding: 6 }}
                onClick={() => setIntervalHandler('inc')}
                data-test-id='increment-interval-btn'
              >
                <PlusIcon className={styles.icons} />
              </Button>
              <Text size={2} weight={400}>
                {intervals}
              </Text>
              <Button
                size='small'
                variant='outline'
                color='neutral'
                style={{ padding: 6 }}
                onClick={() => setIntervalHandler('dec')}
                data-test-id='decrement-interval-btn'
              >
                <MinusIcon className={styles.icons} />
              </Button>
            </Flex>
          </Flex>
        </Show>

        {/* RANGE EDITOR */}
        <Show when={editMode}>
          <Flex gap='small' align='start' style={{ width: '100%' }}>
            <Flex
              direction='column'
              align='start'
              className={styles.rangeWrapper}
            >
              <InputField
                data-error={!rangeValidation}
                data-test-id='change-lower-limit-range'
                type='text'
                value={range[0] ?? 0}
                onChange={e => rangeChangeHandler(e.target.value, range[1])}
              />
            </Flex>
            <Flex
              direction='column'
              align='start'
              className={styles.rangeWrapper}
            >
              <InputField
                label='Max Scale Range'
                data-error={!rangeValidation}
                data-test-id='change-lower-limit-range'
                type='text'
                value={range[1] ?? 0}
                onChange={e => rangeChangeHandler(range[0], e.target.value)}
              />
            </Flex>
          </Flex>
        </Show>
      </Flex>
    </Portal>
  );
};
